import { motion } from "framer-motion";
import { Sparkles, Check } from "lucide-react";
import { TIERS, EXTRA_TIERS } from "./data";
import { EntryTally } from "./EntryTally";

const joinBtn =
  "bg-slate-900 text-cyan-300 hover:shadow-[0_0_18px_rgba(0,240,255,0.4)] transition-shadow focus:ring-2 focus:ring-pink-500 focus:outline-none";

const PricingCard = ({ tier, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`pricing-card-${tier.id}`}
    className={`relative flex flex-col bg-white rounded-2xl p-6 pt-14 mt-12 transition-transform duration-300 hover:-translate-y-2 ${
      tier.featured
        ? "border-2 border-pink-500 shadow-[0_0_35px_rgba(255,0,127,0.25)] lg:scale-[1.06] z-10"
        : "border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
    }`}
  >
    {tier.featured && (
      <span
        data-testid="suggested-giving-badge"
        className="absolute -top-[60px] left-1/2 -translate-x-1/2 z-20 bg-pink-500 text-white text-[10px] font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-[0_0_15px_rgba(255,0,127,0.6)] whitespace-nowrap"
      >
        Suggested Giving
      </span>
    )}

    <div
      className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 rounded-full overflow-hidden border-4 border-white"
      style={{ boxShadow: `0 0 22px ${tier.glow}, 0 0 0 2px ${tier.accent}` }}
    >
      <img src={tier.mascot} alt={`${tier.name} mascot`} className="w-full h-full object-cover" />
    </div>

    <h3 className="text-center font-black uppercase tracking-wide text-xl text-slate-900 mt-2">
      {tier.name}
    </h3>
    <p className="text-center mt-1">
      <span className="text-3xl font-black text-slate-900">{tier.price}</span>
      {tier.priceNote && (
        <span className="block text-xs font-semibold text-slate-500 mt-0.5">{tier.priceNote}</span>
      )}
    </p>

    <div
      data-testid={`entries-highlight-${tier.id}`}
      className="mt-4 flex items-center justify-center gap-1.5 rounded-full py-2 px-3 text-xs font-bold uppercase tracking-wide text-slate-900"
      style={{ backgroundColor: `${tier.accent}1A`, border: `1px solid ${tier.accent}` }}
    >
      <Sparkles size={14} style={{ color: tier.accent }} className="shrink-0" />
      {tier.highlight}
    </div>

    <ul className="mt-5 space-y-2.5 flex-1">
      {tier.perks.map((perk, i) => (
        <li key={i} className="flex items-start gap-2 text-sm font-medium text-slate-600 leading-snug">
          <Check size={15} className="mt-0.5 shrink-0" style={{ color: tier.accent }} />
          {perk}
        </li>
      ))}
    </ul>

    {tier.id === "general" ? (
      <div className="mt-6 grid grid-cols-2 gap-2">
        <a
          href={tier.urlSingle}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="join-now-button-general-single"
          className={`py-3 rounded-full font-bold uppercase tracking-wider text-[11px] text-center block ${joinBtn}`}
        >
          Single $31
        </a>
        <a
          href={tier.urlDual}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="join-now-button-general-dual"
          className={`py-3 rounded-full font-bold uppercase tracking-wider text-[11px] text-center block ${joinBtn}`}
        >
          Dual $62
        </a>
      </div>
    ) : (
      <a
        href={tier.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`join-now-button-${tier.id}`}
        className={`mt-6 w-full py-3 rounded-full font-bold uppercase tracking-widest text-xs text-center block ${
          tier.featured
            ? "bg-pink-500 text-white hover:shadow-[0_0_20px_rgba(255,0,127,0.6)] transition-shadow focus:ring-2 focus:ring-pink-500 focus:outline-none"
            : joinBtn
        }`}
      >
        Join Now
      </a>
    )}
  </motion.div>
);

const ExtraTierCard = ({ tier, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: index * 0.12 }}
    data-testid={`pricing-card-${tier.id}`}
    className="relative flex flex-col sm:flex-row items-center gap-5 bg-white rounded-2xl p-6 sm:p-7 border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:-translate-y-1.5 transition-transform duration-300"
  >
    <div
      className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shrink-0"
      style={{ boxShadow: `0 0 22px ${tier.glow}, 0 0 0 2px ${tier.accent}` }}
    >
      <img src={tier.mascot} alt={`${tier.name} mascot`} className="w-full h-full object-cover" />
    </div>
    <div className="flex-1 text-center sm:text-left">
      <h3 className="font-black uppercase tracking-wide text-lg text-slate-900">
        {tier.name}{" "}
        <span className="text-2xl font-black" style={{ color: tier.accent }}>
          {tier.price}
        </span>
      </h3>
      {tier.highlight && (
        <p
          data-testid={`entries-highlight-${tier.id}`}
          className="mt-2 inline-flex items-start gap-1.5 rounded-2xl py-1.5 px-3 text-[11px] font-bold uppercase tracking-wide text-slate-900 text-left"
          style={{ backgroundColor: `${tier.accent}1A`, border: `1px solid ${tier.accent}` }}
        >
          <Sparkles size={13} style={{ color: tier.accent }} className="shrink-0 mt-0.5" />
          <span>{tier.highlight}</span>
        </p>
      )}
      <ul className="mt-3 space-y-1.5">
        {tier.perks.map((perk, i) => (
          <li key={i} className="flex items-start gap-2 text-sm font-medium text-slate-600 leading-snug justify-center sm:justify-start text-left">
            <Check size={15} className="mt-0.5 shrink-0" style={{ color: tier.accent }} />
            {perk}
          </li>
        ))}
      </ul>
    </div>
    <a
      href={tier.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`join-now-button-${tier.id}`}
      className={`w-full sm:w-auto px-8 py-3 rounded-full font-bold uppercase tracking-widest text-xs text-center shrink-0 ${joinBtn}`}
    >
      Join Now
    </a>
  </motion.div>
);

export const Pricing = () => (
  <section id="tiers" data-testid="pricing-section" className="relative py-24 lg:py-32 grid-bg">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="max-w-2xl"
      >
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-pink-500">02 · Membership Tiers</p>
        <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tight text-slate-900 uppercase">
          Choose your <span className="neon-cyan-shadow">bird</span>.
          <br />
          Boost your impact.
        </h2>
        <p className="mt-4 text-base md:text-lg font-medium text-slate-600 leading-relaxed">
          Every tier stacks the perks below it — and every membership directly
          powers our students, teachers, and campus programs.
        </p>
      </motion.div>

      <EntryTally />

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start max-w-md sm:max-w-none mx-auto sm:mx-0">
        {TIERS.map((tier, i) => (
          <PricingCard key={tier.id} tier={tier} index={i} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mt-16"
      >
        <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight text-slate-900">
          More ways to <span className="text-pink-500">join the flock</span>
        </h3>
      </motion.div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {EXTRA_TIERS.map((tier, i) => (
          <ExtraTierCard key={tier.id} tier={tier} index={i} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm font-semibold"
      >
        <a href="https://www.ashleyfallspta.com/form/m/398559" target="_blank" rel="noopener noreferrer" data-testid="faculty-staff-link" className="text-slate-600 underline decoration-pink-400 decoration-2 underline-offset-4 hover:text-pink-600 transition-colors">
          Faculty / Staff ($15)
        </a>
      </motion.div>
    </div>
  </section>
);
